import { formatTimeDisplay } from "@/lib/utils/time";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  eanNumberError,
  formatOrgNumber,
  formatPeppolId,
  needsInvoiceDetails,
} from "@/lib/utils/company-info";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { AVAILABLE_LANGUAGES } from "@/lib/constants/languages";
import { getBookingStatusDisplay, STATUS_IDS } from "@/lib/constants/statuses";
import { INVOICE_REFERENCE_MAX_LENGTH } from "@/lib/constants/accounting";

// Phone validation helper - only validates, doesn't modify
function validatePhone(phone: string): {
  isValid: boolean;
  error?: string;
} {
  // Handle empty input
  if (!phone || typeof phone !== "string") {
    return {
      isValid: true, // Empty is valid (optional field)
      error: undefined,
    };
  }

  // Clean the input - only for validation, not modification
  const cleaned = phone.trim();

  // Handle empty input after trimming
  if (!cleaned) {
    return {
      isValid: true, // Empty is valid (optional field)
      error: undefined,
    };
  }

  // Extract digits only for pattern checking
  const digitsOnly = cleaned.replace(/[^\d]/g, "");

  // Check for obviously invalid patterns
  if (
    digitsOnly.match(/^0+$/) || // All zeros
    digitsOnly.match(/^1+$/) || // All ones
    digitsOnly.match(/^2+$/) || // All twos
    digitsOnly.match(/^3+$/) || // All threes
    digitsOnly.match(/^4+$/) || // All fours
    digitsOnly.match(/^5+$/) || // All fives
    digitsOnly.match(/^6+$/) || // All sixes
    digitsOnly.match(/^7+$/) || // All sevens
    digitsOnly.match(/^8+$/) || // All eights
    digitsOnly.match(/^9+$/) || // All nines
    digitsOnly === "123456789" || // Sequential test number
    digitsOnly === "1234567890" // Another test pattern
  ) {
    return {
      isValid: false,
      error: "Please enter a valid phone number",
    };
  }

  // Check length
  if (digitsOnly.length < 7) {
    return {
      isValid: false,
      error: "Phone number is too short",
    };
  }

  if (digitsOnly.length > 15) {
    return {
      isValid: false,
      error: "Phone number is too long",
    };
  }

  // Basic format check - allow various formats
  // Allow: +, spaces, dashes, parentheses, and digits
  const basicFormatRegex = /^[\d\s\-\(\)\+]+$/;
  if (!basicFormatRegex.test(cleaned)) {
    return {
      isValid: false,
      error: "Phone number contains invalid characters",
    };
  }

  return {
    isValid: true,
  };
}

// Format phone to E.164 format for Supabase auth.users
function formatPhoneToE164(phone: string): string {
  if (!phone || typeof phone !== "string") {
    return "";
  }

  let formattedPhone = phone.trim();

  // If empty, return empty
  if (!formattedPhone) {
    return "";
  }

  // If phone already starts with +, use as is (assume it's already in E.164 format)
  if (formattedPhone.startsWith("+")) {
    // Clean up any non-digit characters except the leading +
    return "+" + formattedPhone.slice(1).replace(/\D/g, "");
  }

  // For numbers without country code, add + and clean up
  // Remove all non-digit characters
  const digitsOnly = formattedPhone.replace(/\D/g, "");

  // Add + prefix for E.164 format
  return "+" + digitsOnly;
}

// Format phone number for display
function formatPhoneForDisplay(phone: string): string {
  if (!phone || typeof phone !== "string") {
    return "";
  }

  const cleaned = phone.trim();

  // If empty, return empty
  if (!cleaned) {
    return "";
  }

  // Extract digits only
  const digitsOnly = cleaned.replace(/\D/g, "");

  // If the number starts with a country code, format accordingly
  if (cleaned.startsWith("+") || digitsOnly.length > 10) {
    // International format
    if (digitsOnly.startsWith("1") && digitsOnly.length === 11) {
      // US/Canada format: +1 (XXX) XXX-XXXX
      return `+${digitsOnly.slice(0, 1)} (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
    } else if (digitsOnly.startsWith("45") && digitsOnly.length === 10) {
      // Denmark format: +45 XX XX XX XX
      return `+${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 6)} ${digitsOnly.slice(6, 8)} ${digitsOnly.slice(8)}`;
    } else if (digitsOnly.startsWith("46") && digitsOnly.length === 11) {
      // Sweden format: +46 XX XXX XX XX
      return `+${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7, 9)} ${digitsOnly.slice(9)}`;
    } else if (digitsOnly.startsWith("47") && digitsOnly.length === 10) {
      // Norway format: +47 XXX XX XXX
      return `+${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 5)} ${digitsOnly.slice(5, 7)} ${digitsOnly.slice(7)}`;
    } else {
      // Generic international format: group in chunks of 3-4
      let formatted = "+";
      let i = 0;

      // Country code (1-3 digits)
      if (digitsOnly.length >= 11) {
        formatted += digitsOnly.slice(0, 1) + " ";
        i = 1;
      } else if (digitsOnly.length >= 12) {
        formatted += digitsOnly.slice(0, 2) + " ";
        i = 2;
      } else if (digitsOnly.length >= 13) {
        formatted += digitsOnly.slice(0, 3) + " ";
        i = 3;
      } else {
        formatted += digitsOnly.slice(0, 2) + " ";
        i = 2;
      }

      // Format the rest in groups
      while (i < digitsOnly.length) {
        if (i + 4 <= digitsOnly.length) {
          formatted += digitsOnly.slice(i, i + 4) + " ";
          i += 4;
        } else {
          formatted += digitsOnly.slice(i);
          break;
        }
      }

      return formatted.trim();
    }
  } else if (digitsOnly.length === 10) {
    // US format without country code: (XXX) XXX-XXXX
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  } else if (digitsOnly.length === 8) {
    // Danish/Nordic format without country code: XX XX XX XX
    return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 6)} ${digitsOnly.slice(6)}`;
  } else {
    // Return as-is if format is unknown
    return cleaned;
  }
}

// Edit Booking Status Modal
interface EditBookingStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedStatus: string;
  setSelectedStatus: (status: string) => void;
  statusNote: string;
  setStatusNote: (note: string) => void;
  bookingStatuses: any[];
  onConfirm: () => void;
  showStartTime?: string; // Add optional show start time prop
}

export function EditBookingStatusModal({
  isOpen,
  onClose,
  selectedStatus,
  setSelectedStatus,
  statusNote,
  setStatusNote,
  bookingStatuses,
  onConfirm,
  showStartTime,
}: EditBookingStatusModalProps) {
  // Check if selected status is Complete and booking is in the future
  const isFutureComplete = () => {
    if (parseInt(selectedStatus) === STATUS_IDS.BOOKING_COMPLETED && showStartTime) {
      const showStartDate = new Date(showStartTime);
      const now = new Date();
      return showStartDate > now;
    }
    return false;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change booking status</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>New status</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select a status" />
              </SelectTrigger>
              <SelectContent>
                {bookingStatuses.map((status) => (
                  <SelectItem key={status.id} value={status.id.toString()}>
                    {getBookingStatusDisplay(status.name, status.id)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isFutureComplete() && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-800 text-sm font-medium">
                ⚠️ Warning: Cannot complete future bookings
              </p>
              <p className="text-red-700 text-sm mt-1">
                This booking is scheduled for the future. Bookings can only be marked as completed after the show time has passed.
              </p>
            </div>
          )}

          <div>
            <Label>Note (optional)</Label>
            <Textarea
              placeholder="Enter a reason for this status change (optional)..."
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isFutureComplete()}
            className={isFutureComplete() ? "opacity-50 cursor-not-allowed" : ""}
          >
            Update status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Payment Status Modal
interface EditPaymentStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedStatus: string;
  setSelectedStatus: (status: string) => void;
  statusNote: string;
  setStatusNote: (note: string) => void;
  paymentStatuses: any[];
  onConfirm: () => void;
}

export function EditPaymentStatusModal({
  isOpen,
  onClose,
  selectedStatus,
  setSelectedStatus,
  statusNote,
  setStatusNote,
  paymentStatuses,
  onConfirm,
}: EditPaymentStatusModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change payment status</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>New status</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select a status" />
              </SelectTrigger>
              <SelectContent>
                {paymentStatuses.map((status) => (
                  <SelectItem key={status.id} value={status.id.toString()}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea
              placeholder="Enter a reason for this status change (optional)..."
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>
            Update status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Cancel Booking Modal
interface CancelBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  cancelReason: string;
  setCancelReason: (reason: string) => void;
  onConfirm: () => void;
  bookingCreatedAt?: string;
  graceHours?: number;
  venueDefaultLanguage?: string;
}

export function CancelBookingModal({
  isOpen,
  onClose,
  cancelReason,
  setCancelReason,
  onConfirm,
  bookingCreatedAt,
  graceHours = 24,
  venueDefaultLanguage = "en",
}: CancelBookingModalProps) {
  // Use venue's default language for time formatting (24h for Nordic languages)
  const locale = venueDefaultLanguage;

  // Calculate grace period info
  const getGracePeriodInfo = () => {
    if (!bookingCreatedAt) return null;

    const createdAt = new Date(bookingCreatedAt);
    const now = new Date();
    const hoursElapsed =
      (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
    const isInGracePeriod = hoursElapsed <= graceHours;
    const gracePeriodEnd = new Date(
      createdAt.getTime() + graceHours * 60 * 60 * 1000,
    );

    return {
      isInGracePeriod,
      gracePeriodEnd,
      hoursElapsed: Math.round(hoursElapsed * 10) / 10,
    };
  };

  const gracePeriodInfo = getGracePeriodInfo();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel booking</DialogTitle>
          <DialogDescription>
            This action cannot be undone. Please provide a reason for
            cancellation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Grace Period Information */}
          {gracePeriodInfo && (
            <div
              className={`p-3 rounded-lg border ${gracePeriodInfo.isInGracePeriod ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}
            >
              <p className="text-sm font-medium mb-1">
                {gracePeriodInfo.isInGracePeriod
                  ? "✓ Within grace period"
                  : "⚠️ Grace period expired"}
              </p>
              {gracePeriodInfo.isInGracePeriod ? (
                <p className="text-xs text-muted-foreground">
                  Free cancellation until{" "}
                  {gracePeriodInfo.gracePeriodEnd.toLocaleDateString()} at{" "}
                  {formatTimeDisplay(
                    gracePeriodInfo.gracePeriodEnd.toTimeString().slice(0, 5),
                    locale
                  )}
                  <span className="block mt-1">
                    ({graceHours}-hour grace period •{" "}
                    {gracePeriodInfo.hoursElapsed}h elapsed)
                  </span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  The {graceHours}-hour grace period ended on{" "}
                  {gracePeriodInfo.gracePeriodEnd.toLocaleDateString()} at{" "}
                  {formatTimeDisplay(
                    gracePeriodInfo.gracePeriodEnd.toTimeString().slice(0, 5),
                    locale
                  )}
                </p>
              )}
            </div>
          )}

          <div>
            <Label>Cancellation reason (required)</Label>
            <Textarea
              placeholder="Enter the reason for cancelling this booking..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Keep booking
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={!cancelReason.trim()}
          >
            Cancel booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Archive Booking Modal
interface ArchiveBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  archiveReason: string;
  setArchiveReason: (reason: string) => void;
  onConfirm: () => void;
}

export function ArchiveBookingModal({
  isOpen,
  onClose,
  archiveReason,
  setArchiveReason,
  onConfirm,
}: ArchiveBookingModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive booking</DialogTitle>
          <DialogDescription>
            This will archive the booking with pending status. The booking will
            no longer be active but can be referenced for records.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Archive reason (required)</Label>
            <Textarea
              placeholder="Enter the reason for archiving this booking..."
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={!archiveReason.trim()}>
            Archive booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Send Email Modal
interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailSubject: string;
  setEmailSubject: (subject: string) => void;
  emailBody: string;
  setEmailBody: (body: string) => void;
  customerEmail: string;
  bookingId: number | string;
  bookingLanguage?: string;
}

export function SendEmailModal({
  isOpen,
  onClose,
  emailSubject,
  setEmailSubject,
  emailBody,
  setEmailBody,
  customerEmail,
  bookingId,
}: SendEmailModalProps) {
  const [isSending, setIsSending] = useState(false);

  // Generate customer portal link for the booking
  const getCustomerPortalLink = () => {
    const baseUrl = process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000';
    return `${baseUrl}/customerportal/bookings/${bookingId}`;
  };

  const portalLink = getCustomerPortalLink();
  const linkDisplay = `View your booking: ${portalLink}`;

  const handleSend = async () => {
    setIsSending(true);
    try {
      // Convert bookingId to number and validate
      const numericBookingId = typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId;

      if (isNaN(numericBookingId) || numericBookingId <= 0) {
        alert(`Invalid booking ID: ${bookingId}`);
        setIsSending(false);
        return;
      }

      // Combine message with portal link at the end
      const fullBody = `${emailBody.trim()}\n\n${linkDisplay}`;

      const response = await fetch("/api/bookings/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bookingId: numericBookingId,
          to: customerEmail,
          subject: emailSubject,
          body: fullBody,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Success toast will be shown from parent component
        onClose();
      } else {
        const errorMessage = result.error || "Unknown error";
        // If there are validation details, include them
        const detailsMessage = result.details ?
          `\n\nDetails: ${JSON.stringify(result.details, null, 2)}` : "";
        alert("Failed to send email: " + errorMessage + detailsMessage);
      }
    } catch (error) {
      alert("Failed to send email. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Send email to customer</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>To</Label>
            <Input value={customerEmail} disabled />
          </div>
          <div>
            <Label>Subject</Label>
            <Input
              placeholder="Enter email subject..."
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea
              placeholder="Write your message here..."
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              rows={8}
            />
          </div>
          <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
            <Label className="text-sm font-medium text-gray-700 block mb-2">Portal Link (auto-included)</Label>
            <div className="text-sm text-gray-600 break-all p-2 bg-white rounded border border-gray-200">
              {linkDisplay}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!emailSubject.trim() || !emailBody.trim() || isSending}
          >
            {isSending ? "Sending..." : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Host Modal
interface EditHostModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedHost: string;
  setSelectedHost: (host: string) => void;
  statusNote: string;
  setStatusNote: (note: string) => void;
  availableHosts: any[];
  onConfirm: () => void;
}

export function EditHostModal({
  isOpen,
  onClose,
  selectedHost,
  setSelectedHost,
  statusNote,
  setStatusNote,
  availableHosts,
  onConfirm,
}: EditHostModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change host</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>New host</Label>
            <Select value={selectedHost} onValueChange={setSelectedHost}>
              <SelectTrigger>
                <SelectValue placeholder="Select a host" />
              </SelectTrigger>
              <SelectContent>
                {!availableHosts || availableHosts.length === 0 ? (
                  <div className="p-2 text-sm text-gray-500 text-center">
                    No users available to assign as host
                  </div>
                ) : (
                  availableHosts.map((host) => (
                    <SelectItem
                      key={host.user_id || host.id}
                      value={(host.user_id || host.id).toString()}
                    >
                      {host.name || "Unknown User"} ({host.email || "No email"})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea
              placeholder="Enter a reason for this host change (optional)..."
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!selectedHost}
          >
            Update host
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Client Modal
interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientFormData: {
    fullName: string;
    email: string;
    phone: string;
    participants?: number;
  };
  setClientFormData: (data: any) => void;
  onConfirm: (formattedData: {
    fullName: string;
    email: string;
    phone: string;
    participants?: number;
  }) => void | Promise<void>;
  isRoadShow?: boolean;
}

export function EditClientModal({
  isOpen,
  onClose,
  clientFormData,
  setClientFormData,
  onConfirm,
  isRoadShow = false,
}: EditClientModalProps) {
  const [phoneError, setPhoneError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [displayPhone, setDisplayPhone] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Validate initial values when modal opens
  useEffect(() => {
    if (isOpen) {
      validatePhoneInput(clientFormData.phone);
      validateEmail(clientFormData.email);
      // Format phone for display
      setDisplayPhone(formatPhoneForDisplay(clientFormData.phone));
    }
  }, [isOpen, clientFormData.phone, clientFormData.email]);

  // Validate phone in real-time
  const validatePhoneInput = (phone: string): boolean => {
    if (!phone) {
      setPhoneError("");
      return true; // Empty is valid (optional field)
    }

    const phoneResult = validatePhone(phone);

    if (!phoneResult.isValid) {
      setPhoneError(phoneResult.error || "Invalid phone number");
      return false;
    }

    setPhoneError("");
    return true;
  };

  // Validate email in real-time
  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError("");
      return true; // Empty is valid (optional field)
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }

    setEmailError("");
    return true;
  };

  const handlePhoneChange = (value: string) => {
    setClientFormData({
      ...clientFormData,
      phone: value,
    });

    // Format for display
    setDisplayPhone(value);

    // Validate in real-time
    validatePhoneInput(value);
  };

  const handlePhoneBlur = () => {
    // Format phone number on blur for better display
    const formatted = formatPhoneForDisplay(clientFormData.phone);
    setDisplayPhone(formatted);
  };

  const handleEmailChange = (value: string) => {
    setClientFormData({
      ...clientFormData,
      email: value,
    });

    // Validate in real-time
    validateEmail(value);
  };

  const validateAndConfirm = async () => {
    let isValid = true;

    // Validate email
    if (clientFormData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(clientFormData.email)) {
        setEmailError("Please enter a valid email address");
        isValid = false;
      }
    }

    // Validate phone
    if (clientFormData.phone) {
      const phoneResult = validatePhone(clientFormData.phone);

      if (!phoneResult.isValid) {
        setPhoneError(phoneResult.error || "Invalid phone number");
        isValid = false;
      }
    }

    if (isValid) {
      setIsUpdating(true);
      try {
        // Format phone to E.164 before sending to backend
        const formattedPhone = clientFormData.phone
          ? formatPhoneToE164(clientFormData.phone)
          : "";

        // Update the local state for UI
        setClientFormData({
          ...clientFormData,
          phone: formattedPhone,
        });

        // Call onConfirm with the formatted data directly
        await onConfirm({
          fullName: clientFormData.fullName,
          email: clientFormData.email,
          phone: formattedPhone,
          participants: clientFormData.participants,
        });
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit client information</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input
              placeholder="Enter client's full name"
              value={clientFormData.fullName}
              onChange={(e) =>
                setClientFormData({
                  ...clientFormData,
                  fullName: e.target.value,
                })
              }
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              placeholder="Enter client's email"
              value={clientFormData.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              className={emailError ? "border-red-500" : ""}
            />
            {emailError && (
              <p className="text-sm text-red-500 mt-1">{emailError}</p>
            )}
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              type="tel"
              placeholder="Enter phone number (e.g., +45 12 34 56 78)"
              value={displayPhone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onBlur={handlePhoneBlur}
              className={phoneError ? "border-red-500" : ""}
            />
            {phoneError ? (
              <p className="text-sm text-red-500 mt-1">{phoneError}</p>
            ) : (
              <p className="text-sm text-gray-500 mt-1">
                Phone will be formatted to international format (+country code)
              </p>
            )}
          </div>
          {isRoadShow && (
            <div>
              <Label>Participants</Label>
              <Input
                type="number"
                placeholder="Number of participants"
                value={clientFormData.participants || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                  setClientFormData({
                    ...clientFormData,
                    participants: value,
                  });
                }}
                min="1"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={validateAndConfirm}
            disabled={
              isUpdating || !!phoneError || !!emailError || !clientFormData.fullName.trim()
            }
          >
            {isUpdating ? "Updating..." : "Update client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Company Modal
interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyFormData: {
    companyName: string;
    organizationNumber: string;
    invoiceEmail: string;
    invoiceReference: string;
    invoiceType: "pdf_invoice" | "e_invoice";
    peppolId: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    postcode: string;
    country: string;
  };
  setCompanyFormData: (data: any) => void;
  onConfirm: () => void | Promise<void>;
  /** Venue currency; Danish venues (DKK) label the org. number as "CVR number". */
  venueCurrency?: string | null;
  /** Venue default language; Danish-language venues also use "CVR number". */
  venueDefaultLanguage?: string | null;
  /**
   * The booking's payment method. Only invoice-paid bookings need the
   * address and invoicing fields; a company paying by card just needs its
   * name and organization number.
   */
  paymentMethod?: string | null;
  /**
   * Partner picker for the booking. Omitted where staff may not change the
   * partner (bar staff).
   */
  partner?: {
    options: { id: number; company_name: string; commission_percentage: number }[];
    /** The booking's partner as saved, kept selectable if it was deactivated. */
    current?: { id: number; company_name: string } | null;
    selectedId: number | null;
    onSelect: (id: number | null) => void;
    endCustomerName: string;
    onEndCustomerNameChange: (value: string) => void;
  };
}

export function EditCompanyModal({
  isOpen,
  onClose,
  companyFormData,
  setCompanyFormData,
  onConfirm,
  venueCurrency,
  venueDefaultLanguage,
  paymentMethod,
  partner,
}: EditCompanyModalProps) {
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const partnerOptions = partner
    ? [
        ...partner.options,
        ...(partner.current &&
        !partner.options.some((o) => o.id === partner.current!.id)
          ? [{ ...partner.current, commission_percentage: NaN }]
          : []),
      ]
    : [];
  const invoiceRequired = needsInvoiceDetails(paymentMethod);
  // Marks a field that is only mandatory when the booking is invoiced.
  const inv = invoiceRequired ? " *" : "";

  // Danish venues use "CVR number" — identified by DKK currency OR a Danish
  // default language (covers venues that bill in another currency, e.g. SEK).
  const isDanish =
    venueCurrency === "DKK" ||
    (venueDefaultLanguage ?? "").toLowerCase().startsWith("da");
  const orgNumberLabel = isDanish ? "CVR number" : "Organization number";
  const orgNumberPlaceholder = isDanish
    ? "Enter CVR number"
    : "Enter organization number";
  // A Danish venue gets an EAN number field under the CVR, for public
  // institutions. It is the Peppol ID field, so it isn't shown again
  // further down.
  const showEan = isDanish;
  const eanError = showEan ? eanNumberError(companyFormData.peppolId) : null;

  const handleConfirm = async () => {
    setIsUpdating(true);
    try {
      await onConfirm();
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit company information</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!invoiceRequired && (
            <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
              This booking is paid by {paymentMethod}, so only the company
              name and {orgNumberLabel.toLowerCase()} are needed. Address and
              invoice details are optional and can be added later.
            </p>
          )}
          {/* Partner agency that brokered the booking */}
          {partner && partnerOptions.length > 0 && (
            <div className="space-y-3 rounded-md border p-3">
              <div>
                <Label htmlFor="edit-partner-select">
                  Partner{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    (optional — fills in the company info below)
                  </span>
                </Label>
                <Select
                  value={
                    partner.selectedId !== null
                      ? String(partner.selectedId)
                      : "__none__"
                  }
                  onValueChange={(value) =>
                    partner.onSelect(
                      value === "__none__" ? null : parseInt(value, 10),
                    )
                  }
                >
                  <SelectTrigger id="edit-partner-select">
                    <SelectValue placeholder="No partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No partner</SelectItem>
                    {partnerOptions.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.company_name}
                        {Number.isNaN(p.commission_percentage)
                          ? " (inactive)"
                          : ` (${p.commission_percentage}% commission)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {partner.selectedId !== null && (
                <div>
                  <Label htmlFor="edit-end-customer">
                    End customer{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      (optional — shown on the invoice)
                    </span>
                  </Label>
                  <Input
                    id="edit-end-customer"
                    placeholder="Who the partner is booking for"
                    value={partner.endCustomerName}
                    onChange={(e) =>
                      partner.onEndCustomerNameChange(e.target.value)
                    }
                  />
                </div>
              )}
            </div>
          )}

          {/* Company Basic Info */}
          <div>
            <Label>Company name *</Label>
            <Input
              placeholder="Enter company name"
              value={companyFormData.companyName}
              onChange={(e) =>
                setCompanyFormData({
                  ...companyFormData,
                  companyName: e.target.value,
                })
              }
              required
            />
          </div>

          <div>
            <Label>{orgNumberLabel} *</Label>
            <Input
              placeholder={orgNumberPlaceholder}
              value={companyFormData.organizationNumber}
              onChange={(e) =>
                setCompanyFormData({
                  ...companyFormData,
                  organizationNumber: formatOrgNumber(
                    e.target.value,
                    companyFormData.country,
                  ),
                })
              }
            />
          </div>

          {showEan && (
            <div>
              <Label>EAN number</Label>
              <Input
                placeholder="5798000000000 (optional, for public institutions)"
                inputMode="numeric"
                value={companyFormData.peppolId}
                onChange={(e) =>
                  setCompanyFormData({
                    ...companyFormData,
                    peppolId: formatPeppolId(e.target.value, true),
                  })
                }
                className={eanError ? "border-red-500" : ""}
              />
              {eanError && (
                <p className="mt-1 text-sm text-red-500">{eanError}</p>
              )}
            </div>
          )}

          {/* Company Address */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Address line 1{inv}</Label>
              <Input
                placeholder="Street address"
                value={companyFormData.addressLine1}
                onChange={(e) =>
                  setCompanyFormData({
                    ...companyFormData,
                    addressLine1: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label>Address line 2 (optional)</Label>
              <Input
                placeholder="Apartment, suite, etc."
                value={companyFormData.addressLine2}
                onChange={(e) =>
                  setCompanyFormData({
                    ...companyFormData,
                    addressLine2: e.target.value,
                  })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City{inv}</Label>
                <Input
                  placeholder="City"
                  value={companyFormData.city}
                  onChange={(e) =>
                    setCompanyFormData({
                      ...companyFormData,
                      city: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label>Postcode{inv}</Label>
                <Input
                  placeholder="Postcode"
                  value={companyFormData.postcode}
                  onChange={(e) =>
                    setCompanyFormData({
                      ...companyFormData,
                      postcode: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div>
              <Label>Country{inv}</Label>
              <Select
                value={companyFormData.country}
                onValueChange={(value) =>
                  setCompanyFormData({
                    ...companyFormData,
                    country: value,
                    // The org number's format follows the company's country.
                    organizationNumber: formatOrgNumber(
                      companyFormData.organizationNumber,
                      value,
                    ),
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DK">Denmark</SelectItem>
                  <SelectItem value="SE">Sweden</SelectItem>
                  <SelectItem value="NO">Norway</SelectItem>
                  <SelectItem value="FI">Finland</SelectItem>
                  <SelectItem value="IS">Iceland</SelectItem>
                  <SelectItem value="DE">Germany</SelectItem>
                  <SelectItem value="GB">United Kingdom</SelectItem>
                  <SelectItem value="US">United States</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Invoice Information */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Invoice type{inv}</Label>
              <Select
                value={companyFormData.invoiceType}
                onValueChange={(value: "pdf_invoice" | "e_invoice") =>
                  setCompanyFormData({
                    ...companyFormData,
                    invoiceType: value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select invoice type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf_invoice">PDF Invoice</SelectItem>
                  <SelectItem value="e_invoice">E-Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {companyFormData.invoiceType === "e_invoice" && !showEan && (
              <div>
                <Label>Peppol id</Label>
                <Input
                  placeholder="Enter Peppol ID for e-invoicing"
                  value={companyFormData.peppolId}
                  onChange={(e) =>
                    setCompanyFormData({
                      ...companyFormData,
                      peppolId: e.target.value,
                    })
                  }
                />
              </div>
            )}

            <div>
              <Label>Invoice email{inv}</Label>
              <Input
                type="email"
                placeholder="Enter invoice email"
                value={companyFormData.invoiceEmail}
                onChange={(e) =>
                  setCompanyFormData({
                    ...companyFormData,
                    invoiceEmail: e.target.value,
                  })
                }
                required={invoiceRequired}
              />
            </div>

            <div>
              <Label>Invoice reference{inv}</Label>
              <Input
                placeholder="Enter invoice reference"
                value={companyFormData.invoiceReference}
                maxLength={INVOICE_REFERENCE_MAX_LENGTH}
                onChange={(e) =>
                  setCompanyFormData({
                    ...companyFormData,
                    invoiceReference: e.target.value,
                  })
                }
                required={invoiceRequired}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isUpdating || eanError !== null}
          >
            {isUpdating ? "Updating..." : "Update company"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Payment Preference Modal
interface EditPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedMethod: string;
  setSelectedMethod: (method: string) => void;
  statusNote: string;
  setStatusNote: (note: string) => void;
  onConfirm: () => void;
  isCorporate?: boolean;
  isRoadShow?: boolean;
  isLoading?: boolean;
}

export function EditPaymentMethodModal({
  isOpen,
  onClose,
  selectedMethod,
  setSelectedMethod,
  statusNote,
  setStatusNote,
  onConfirm,
  isCorporate = false,
  isRoadShow = false,
  isLoading = false,
}: EditPaymentMethodModalProps) {
  // For road show bookings: Card and Invoice only
  // For corporate bookings: Card and Invoice only
  // For private bookings: Card and Cash only
  const allPaymentMethods = [
    { value: "card", label: "Card" },
    { value: "invoice", label: "Invoice" },
    { value: "swish", label: "Swish" },
    { value: "cash", label: "Cash" },
  ];

  const paymentMethods = isRoadShow
    ? allPaymentMethods.filter(
        (method) => method.value === "card" || method.value === "invoice",
      )
    : isCorporate
    ? allPaymentMethods.filter(
        (method) => method.value === "card" || method.value === "invoice",
      )
    : allPaymentMethods.filter(
        (method) => method.value === "card" || method.value === "cash",
      );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change payment preference</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Payment preference</Label>
            <Select value={selectedMethod} onValueChange={setSelectedMethod} disabled={isLoading}>
              <SelectTrigger disabled={isLoading}>
                <SelectValue placeholder="Select payment preference" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((method) => (
                  <SelectItem key={method.value} value={method.value}>
                    {method.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note</Label>
            <Textarea
              placeholder="Reason for changing payment preference..."
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              rows={3}
              disabled={isLoading}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isLoading}>
            {isLoading ? "Updating..." : "Update payment preference"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit Language Modal
interface EditLanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLanguage: string;
  setSelectedLanguage: (language: string) => void;
  onConfirm: () => void;
  // Deprecated: staff may set any app language regardless of which languages
  // the venue has translations for (bookings.lang only drives the customer's
  // email language, which has its own fallback chain). Kept for caller compat.
  supportedLanguages?: string[];
  isLoading?: boolean;
}

export function EditLanguageModal({
  isOpen,
  onClose,
  selectedLanguage,
  setSelectedLanguage,
  onConfirm,
  isLoading = false,
}: EditLanguageModalProps) {
  // Offer all app languages so staff aren't limited to the venue's configured
  // translations (matches the new-booking cart selector).
  const languages = AVAILABLE_LANGUAGES.map((lang) => ({
    value: lang.code,
    label: `${lang.flag} ${lang.name}`,
  }));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change booking language</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Language</Label>
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage} disabled={isLoading}>
              <SelectTrigger disabled={isLoading}>
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                {languages.map((language) => (
                  <SelectItem key={language.value} value={language.value}>
                    {language.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isLoading || !selectedLanguage}>
            {isLoading ? "Updating..." : "Update language"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
